import { ObjectSvg, PlacedObject } from '@bcl/drawing-control';
import { LocationDetailsOptions, Monitor } from '../area/area.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import * as monitorIcons from './monitor-icons';

export type MonitorMapObject = PlacedObject<string, Monitor>;
export type MapLegend = Array<{
  label: string;
  svg: { content: SafeHtml; size: { width: number; height: number } };
}>;

const buildMonitorType = (
  id: string | null,
  temporary: boolean,
  outside: boolean,
  toxic: boolean | undefined,
  multiple: boolean,
  done: boolean
): string => {
  const idType = id ? id : '';
  const temporaryType = temporary ? '_temporary' : '';
  const outsideType = outside ? '_outside' : '';
  const toxicType = toxic ? '_toxic' : '';
  const multipleType = multiple ? '_multiple' : '';
  const doneType = done ? '_done' : '';
  const monitorType = `${idType}${temporaryType}${outsideType}${toxicType}${multipleType}${doneType}`;
  return monitorType;
};

export const mapMonitorsToMapObjects = (
  monitors: Monitor[]
): MonitorMapObject[] => {
  const hasCoordinates = (
    monitor: Monitor
  ): monitor is Required<Pick<Monitor, 'coordinates'>> & Monitor =>
    !!monitor.coordinates;

  return monitors?.filter(hasCoordinates)?.map((monitor) => ({
    id: monitor.id.toString(),
    type: buildMonitorType(
      monitor.id?.toString(),
      monitor.temporary,
      monitor.outside,
      monitor.toxic,
      monitor.multiple,
      monitor.statusId === '1' && monitor.type === 'Action'
    ),
    coordinates: {
      x: monitor.coordinates.x,
      y: monitor.coordinates.y,
    },
    data: monitor,
  }));
};

@Injectable()
export class AreaMapObjectsService {
  constructor(private domSanitizer: DomSanitizer) {}

  public buildMonitorsSvg(
    mapMonitors: MonitorMapObject[],
    locationDetailsOptions: LocationDetailsOptions
  ): { monitorSvg: any; legend: any[] } {
    let monitorSvg: Record<string, ObjectSvg> = {};
    const legend: any[] = [];

    for (const monitor of mapMonitors) {
      let iconType = null;

      for (const x of locationDetailsOptions.monitor.groups) {
        const include = x.additionalValues?.groups?.includes(
          monitor.data.typeId
        );

        if (include) {
          iconType = x.additionalValues?.icon;

          if (
            !legend.find((item) => item.label === x.name) &&
            monitorIcons.monitors[x.additionalValues?.icon]?.default
          ) {
            const legendItem = {
              label: x.name,
              svg: {
                content: this.domSanitizer.bypassSecurityTrustHtml(
                  monitorIcons.monitors[x.additionalValues?.icon].default.svg
                ),
                size: monitorIcons.monitors[x.additionalValues?.icon].default
                  .size,
              },
            };

            legend.push(legendItem);
          }

          break;
        }
      }

      if (iconType) {
        const iconVariantType =
          monitor.type.split('_').length <= 1
            ? 'default'
            : monitor.type.split('_').slice(1).join('_');

        let selectedMonitor =
          monitorIcons.monitors[iconType]?.[iconVariantType];

        if (!selectedMonitor && monitorIcons.monitors?.[iconType]?.default) {
          selectedMonitor = monitorIcons.monitors[iconType].default;
          console.warn(
            `The icon "${iconType} ${iconVariantType}" should be displayed but it does not exist so the default one was used`
          );
        } else {
          console.warn(
            `The icon "${iconType} ${iconVariantType}" should be displayed but it does not exist`
          );
        }

        if (selectedMonitor) {
          const monitorText = String(
            monitor.data.monitorName || monitor.data.monitorNumber || ''
          ).trimEnd();

          let svgWithText = selectedMonitor.svg;
          if (monitorText) {
            const monitorTextLength =
              monitorText?.toString().length <= 4
                ? monitorText?.toString().length
                : 4;

            svgWithText = selectedMonitor.svg.replace(
              '<placeholder-for-text>',
              `
                  <rect x="${
                    selectedMonitor.textConfig[monitorTextLength].recX
                  }" y="${
                selectedMonitor.textConfig[monitorTextLength].recY
              }" width="${
                selectedMonitor.textConfig[monitorTextLength].width
              }" height="${
                selectedMonitor.textConfig[monitorTextLength].height
              }" fill="#000" stroke="${
                monitor.data.notes ? '#1E90FF ' : '#fff'
              }" stroke-width="${monitor.data.notes ? '3' : '1.5'}" rx="5"/>
          <text x="${selectedMonitor.textConfig[monitorTextLength].txtX}" y="${
                selectedMonitor.textConfig[monitorTextLength].txtY
              }" font-size="8px" fill="#fff" font-weight="bold">
            ${monitorText}
          </text>
          `
            );
          } else {
            svgWithText = selectedMonitor.svg.replace(
              '<placeholder-for-text>',
              ''
            );
          }
          const svgWithBackgroundColor =
            monitor.data.catchStatusColor === undefined ||
            monitor.data.catchStatusColor === null ||
            monitor.data.catchStatusColor === ''
              ? svgWithText.replace('<background-color-placeholder>', '')
              : svgWithText.replace(
                  '<background-color-placeholder>',
                  monitor.data.catchStatusColor
                );

          const monitorSVG = {
            [monitor.type]: {
              content: svgWithBackgroundColor,
              size: selectedMonitor.size,
            },
          };

          monitorSvg = { ...monitorSvg, ...monitorSVG };
        }
      }
    }

    const hasOutsideMonitor = mapMonitors.some(
      (monitor) => monitor.data.outside
    );
    const hasToxicMonitor = mapMonitors.some((monitor) => monitor.data.toxic);

    if (hasOutsideMonitor) {
      const legendItem = {
        label: 'Outside',
        svg: {
          content: this.domSanitizer.bypassSecurityTrustHtml(
            monitorIcons.monitorVariants.outside.default.svg
          ),
          size: monitorIcons.monitorVariants.outside.default.size,
        },
      };

      legend.push(legendItem);
    }

    if (hasToxicMonitor) {
      const legendItem = {
        label: 'Toxic',
        svg: {
          content: this.domSanitizer.bypassSecurityTrustHtml(
            monitorIcons.monitorVariants.toxic.default.svg
          ),
          size: monitorIcons.monitorVariants.toxic.default.size,
        },
      };

      legend.push(legendItem);
    }

    return { monitorSvg, legend };
  }
}
